/**
 * BreadcrumbsDebugOverlay — وضع تصحيح لفتات الخبز.
 *
 * تفعيل:
 *   - إضافة `?bcdebug=1` إلى URL (يحفظ الحالة في localStorage)
 *   - أو ضغط Alt+D لتبديل العرض في أيّ لحظة
 *   - إزالة: `?bcdebug=0` أو Alt+D
 *
 * يعرض:
 *   - المسار الحاليّ (pathname)
 *   - نطاق العرض (scope: public/admin/account)
 *   - سلسلة Breadcrumb المُستخرجة (path + label ar/en + parent)
 *   - تنبيه أحمر لأيّ عنصر مفقود من BREADCRUMBS map
 */
import * as React from "react";
import { useLocation } from "@tanstack/react-router";
import { BREADCRUMBS, type Crumb } from "@/lib/breadcrumbsMap";

const LS_KEY = "lecmo:bc-debug";

function isEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const sp = new URLSearchParams(window.location.search);
    const q = sp.get("bcdebug");
    if (q === "1") {
      window.localStorage.setItem(LS_KEY, "1");
      return true;
    }
    if (q === "0") {
      window.localStorage.removeItem(LS_KEY);
      return false;
    }
    return window.localStorage.getItem(LS_KEY) === "1";
  } catch {
    return false;
  }
}

function resolveChain(pathname: string): Array<{ path: string; entry?: Crumb; missing: boolean }> {
  const clean = pathname.replace(/\/+$/, "") || "/";
  const known = BREADCRUMBS[clean];
  const chain: Array<{ path: string; entry?: Crumb; missing: boolean }> = [];
  if (known) {
    let cursor: string | null | undefined = clean;
    const seen = new Set<string>();
    while (cursor && !seen.has(cursor)) {
      seen.add(cursor);
      const entry: Crumb | undefined = BREADCRUMBS[cursor];
      if (!entry) break;
      chain.unshift({ path: cursor, entry, missing: false });
      cursor = entry.parent;
    }
  } else {
    chain.push({ path: "/", entry: BREADCRUMBS["/"], missing: false });
    const segments = clean.split("/").filter(Boolean);
    let acc = "";
    for (const seg of segments) {
      acc += "/" + seg;
      const e = BREADCRUMBS[acc];
      chain.push({ path: acc, entry: e, missing: !e });
    }
  }
  return chain;
}

export function BreadcrumbsDebugOverlay() {
  const { pathname } = useLocation();
  const [on, setOn] = React.useState<boolean>(() => isEnabled());
  const [collapsed, setCollapsed] = React.useState(false);

  React.useEffect(() => {
    // اقرأ URL عند كل تغيير مسار (على حال URL param جديد)
    setOn(isEnabled());
    function onKey(e: KeyboardEvent) {
      if (e.altKey && (e.key === "d" || e.key === "D")) {
        e.preventDefault();
        try {
          const next = window.localStorage.getItem(LS_KEY) === "1" ? null : "1";
          if (next) window.localStorage.setItem(LS_KEY, next);
          else window.localStorage.removeItem(LS_KEY);
          setOn(!!next);
        } catch {
          /* ignore */
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pathname]);

  const scope: "public" | "admin" | "account" = pathname.startsWith("/admin")
    ? "admin"
    : pathname.startsWith("/account")
      ? "account"
      : "public";
  const chain = React.useMemo(() => resolveChain(pathname), [pathname]);
  const anyMissing = chain.some((c) => c.missing);

  if (!on) return null;

  return (
    <div
      dir="ltr"
      style={{ zIndex: 9999 }}
      className="fixed bottom-3 left-3 max-w-[92vw] rounded-lg border border-amber-400 bg-black/90 p-2 font-mono text-[11px] text-amber-200 shadow-xl backdrop-blur"
    >
      <div className="flex items-center gap-2 border-b border-amber-500/40 pb-1 mb-1">
        <span className="rounded bg-amber-500 px-1.5 py-0.5 font-bold text-black">BC-DEBUG</span>
        <span className="rounded bg-white/10 px-1.5 py-0.5 uppercase tracking-wide">{scope}</span>
        {anyMissing && (
          <span className="rounded bg-red-600 px-1.5 py-0.5 font-bold text-white">MISSING</span>
        )}
        <button
          onClick={() => setCollapsed((v) => !v)}
          className="ms-auto rounded bg-white/10 px-2 py-0.5 hover:bg-white/20"
        >
          {collapsed ? "+" : "–"}
        </button>
        <button
          onClick={() => {
            try {
              window.localStorage.removeItem(LS_KEY);
            } catch {
              /* ignore */
            }
            setOn(false);
          }}
          className="rounded bg-white/10 px-2 py-0.5 hover:bg-red-500/40"
          title="إغلاق (Alt+D)"
        >
          ×
        </button>
      </div>
      <div className="truncate">
        <span className="opacity-60">path: </span>
        <span className="text-white">{pathname}</span>
      </div>
      {!collapsed && (
        <ol className="mt-1 space-y-0.5">
          {chain.map((c, i) => (
            <li key={c.path + i} className="flex items-start gap-2">
              <span className="opacity-50">{i + 1}.</span>
              <span className="min-w-[140px] truncate text-white">{c.path}</span>
              {c.missing ? (
                <span className="rounded bg-red-600/80 px-1 text-white">no entry in BREADCRUMBS</span>
              ) : (
                <>
                  <span className="opacity-70">ar:</span>
                  <span>{c.entry?.ar}</span>
                  <span className="opacity-70">en:</span>
                  <span>{c.entry?.en}</span>
                  <span className="opacity-50">parent: {String(c.entry?.parent ?? "null")}</span>
                </>
              )}
            </li>
          ))}
        </ol>
      )}
      <div className="mt-1 border-t border-amber-500/30 pt-1 text-[10px] opacity-70">
        Alt+D للتبديل · ?bcdebug=1/0 · localStorage: {LS_KEY}
      </div>
    </div>
  );
}
